import { useEffect, useRef, useState } from 'react';
import NoCreditsModal from '@/components/common/NoCreditsModal';
import { AI_CREDITS_REQUIRED_EVENT, type CreditsRequiredEventDetail } from '@/lib/creditsGuard';
import { trackCreditsRequiredUiAction, trackCreditsRequiredUiView } from '@/lib/creditsTracking';
import { CREDIT_PURCHASE_URL, openCreditPurchase } from '@/lib/creditPurchase';

const DEFAULT_COPY =
  "You don't have enough credits for this AI request. Top up your account to continue.";

const GlobalCreditsRequiredModal = () => {
  const [modalCopy, setModalCopy] = useState(DEFAULT_COPY);
  const [modalTitle, setModalTitle] = useState('Not enough credits');
  const [modalDetail, setModalDetail] = useState<CreditsRequiredEventDetail>({});
  const [open, setOpen] = useState(false);
  const openRef = useRef(false);
  const lastTrackedViewKeyRef = useRef('');

  useEffect(() => {
    const handleCreditsRequired = (event: Event) => {
      const detail = (event as CustomEvent<CreditsRequiredEventDetail>).detail || {};
      const nextTitle = detail.title || 'Not enough credits';
      const nextCopy = detail.copy || DEFAULT_COPY;
      const viewKey = JSON.stringify([
        detail.productArea || '',
        detail.surface || '',
        detail.stage || '',
        detail.source || '',
        detail.operation || '',
        detail.taskCode || '',
        detail.trigger || '',
        detail.uiType || 'modal',
        detail.metadata || {},
        nextTitle,
        nextCopy,
      ]);

      if (!openRef.current || lastTrackedViewKeyRef.current !== viewKey) {
        trackCreditsRequiredUiView({
          productArea: detail.productArea,
          surface: detail.surface,
          stage: detail.stage,
          uiType: detail.uiType || 'modal',
          source: detail.source,
          operation: detail.operation,
          taskCode: detail.taskCode,
          trigger: detail.trigger,
          title: nextTitle,
          copy: nextCopy,
          metadata: detail.metadata,
        });
        lastTrackedViewKeyRef.current = viewKey;
      }

      setModalTitle(nextTitle);
      setModalCopy(nextCopy);
      setModalDetail(detail);
      openRef.current = true;
      setOpen(true);
    };

    window.addEventListener(AI_CREDITS_REQUIRED_EVENT, handleCreditsRequired);
    return () => window.removeEventListener(AI_CREDITS_REQUIRED_EVENT, handleCreditsRequired);
  }, []);

  if (!open) return null;

  const closeModal = () => {
    trackCreditsRequiredUiAction({
      productArea: modalDetail.productArea,
      surface: modalDetail.surface,
      stage: modalDetail.stage,
      uiType: modalDetail.uiType || 'modal',
      source: modalDetail.source,
      operation: modalDetail.operation,
      taskCode: modalDetail.taskCode,
      trigger: modalDetail.trigger,
      title: modalTitle,
      copy: modalCopy,
      action: 'cancel',
      metadata: modalDetail.metadata,
    });
    openRef.current = false;
    setOpen(false);
  };

  const getCredits = () => {
    trackCreditsRequiredUiAction({
      productArea: modalDetail.productArea,
      surface: modalDetail.surface,
      stage: modalDetail.stage,
      uiType: modalDetail.uiType || 'modal',
      source: modalDetail.source,
      operation: modalDetail.operation,
      taskCode: modalDetail.taskCode,
      trigger: modalDetail.trigger,
      title: modalTitle,
      copy: modalCopy,
      action: 'get_credits',
      targetRoute: CREDIT_PURCHASE_URL,
      metadata: modalDetail.metadata,
    });
    openRef.current = false;
    setOpen(false);
    openCreditPurchase();
  };

  return (
    <NoCreditsModal
      title={modalTitle}
      copy={modalCopy}
      onClose={closeModal}
      onGetCredits={getCredits}
    />
  );
};

export default GlobalCreditsRequiredModal;
